import React, { useEffect, useState, useMemo } from "react";
import { urlConversion, endpointGetConversions, urlDelivery, endpointCompleteISBN, endpointDownloadIsbnTemplate } from '../services/Endpoints';
import { Loading } from "./Loading";
import Paginator from "./Paginator";
import { ISBNModal } from "./ISBNModal";
import { ActionsMenu } from "./AccountsUsers/ActionsMenu";
import AuthService from '../services/AuthService';

export function ISBNList({ filters, refreshKey }) {
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const itemsPerPage = 10;

    useEffect(() => {
        getConversions();
    }, [refreshKey]);

    const getConversions = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${urlConversion}${endpointGetConversions}`);
            const res = await response.json();
            if (res && res.status == 'success') {
                setProjects(res.data.projects);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    }

    const getISBNStatus = (project) => {
        // Status mapping from Angular: 0=Initial/Registrado, 1=Uploaded/En Trámite, 2=Completed/Asignado
        let status = project.isbn_info?.status;
        if (!status) {
            status = '0';
        }
        return String(status);
    };

    const filteredProjects = useMemo(() => {
        let result = projects.filter(project => {
            const hasService = project.services?.includes('isbn');
            if (!hasService) return false;

            const serviceStatus = getISBNStatus(project);
            const matchStatus = statusFilter === '' || serviceStatus === statusFilter;
            const matchAccount = filters.account === '' || project.account.toLowerCase().includes(filters.account.toLowerCase());
            const matchInkId = filters.ink_id === '' || String(project.ink_id).toLowerCase().includes(filters.ink_id.toLowerCase());
            return matchStatus && matchAccount && matchInkId;
        });

        const orderMap = { '0': 1, '1': 2, '2': 3 };
        result.sort((a, b) => (orderMap[getISBNStatus(a)] || 99) - (orderMap[getISBNStatus(b)] || 99));

        return result;
    }, [projects, filters, statusFilter]);

    const paginatedProjects = useMemo(() => {
        const offset = (currentPage - 1) * itemsPerPage;
        return filteredProjects.slice(offset, offset + itemsPerPage);
    }, [filteredProjects, currentPage]);

    const handleDownloadTemplate = async (project) => {
        try {
            const token = AuthService.getToken();
            const response = await fetch(`${urlConversion}${endpointDownloadIsbnTemplate}?ink_id=${project.ink_id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `plantilla_isbn_${project.ink_id}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                alert('Error al descargar la plantilla');
            }
        } catch (error) {
            console.error('Error downloading template:', error);
        }
    };

    const handleViewUserFile = (project) => {
        const fileUrl = project.isbn_info?.user_file;
        if (fileUrl) {
            window.open(fileUrl, '_blank');
        } else {
            alert('No hay archivo disponible');
        }
    };

    const handleOpenModal = (project) => {
        setSelectedProject(project);
        setIsModalOpen(true);
    };

    const handleSubmitISBN = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());

        try {
            setLoading(true);
            const token = AuthService.getToken();
            const response = await fetch(`${urlConversion}${endpointCompleteISBN}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ink_id: selectedProject.ink_id,
                    account: selectedProject.account,
                    isbn: data.isbn,
                    status: 'asignado'
                })
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                //alert('ISBN guardado exitosamente');
                setIsModalOpen(false);
                await getConversions();
            } else {
                //alert('Error al guardar el ISBN');
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error('Error submitting ISBN:', error);
        }
    };

    return (
        <>
            {loading && <Loading />}

            <div className="row mb-3 mt-4 d-flex justify-content-between align-items-end">
                <div className="col-md-4 col-lg-3">
                    <label className="fw-bold mb-2 small text-uppercase text-white opacity-75">Estado de ISBN</label>
                    <select
                        className="form-select bg-dark text-white border-secondary py-2"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="">Todos los estados</option>
                        <option value="0">Registrado</option>
                        <option value="1">En trámite</option>
                        <option value="2">Asignado</option>
                    </select>
                </div>
            </div>
            <div className="modern-table-container">
                <table className="modern-table striped-table">
                    <thead>
                        <tr>
                            <th>Estado</th><th>Cuenta</th><th>Ink id</th><th>Título</th><th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedProjects.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="text-center py-5 text-white-50">
                                    <h6 className="m-0 opacity-50">No se encontraron resultados para los filtros aplicados</h6>
                                </td>
                            </tr>
                        ) : (
                            paginatedProjects.map((project) => {
                                const serviceStatusId = getISBNStatus(project);

                                let statusLabel = 'Registrado';
                                if (String(serviceStatusId) === '2') statusLabel = 'Asignado';
                                else if (String(serviceStatusId) === '1') statusLabel = 'En trámite';
                                else if (String(serviceStatusId) === '0') statusLabel = 'Registrado';

                                const items = [];
                                if (project.isbn_info?.template) {
                                    items.push({ label: 'Descargar plantilla', onClick: () => handleDownloadTemplate(project) });
                                }
                                if (project.isbn_info?.user_file) {
                                    items.push({ label: 'Descargar archivo', onClick: () => handleViewUserFile(project) });
                                }
                                items.push({ label: 'Asignar ISBN', onClick: () => handleOpenModal(project) });

                                return (
                                    <tr key={`${project.account}-${project.ink_id}`}>
                                        <td className="short">
                                            <span className={`badge-status status-${serviceStatusId}`}>
                                                {statusLabel}
                                            </span>
                                        </td>
                                        <td className="short">{project.account}</td>
                                        <td className="short">{project.ink_id}</td>
                                        <td>{project.title}</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                {!project.isbn_info?.template && !project.isbn_info?.user_file && (
                                                    <span className="small text-white-50 opacity-50 px-2 border border-secondary rounded" style={{ fontSize: '10px' }}>
                                                        Plantilla no cargada
                                                    </span>
                                                )}
                                                <ActionsMenu items={items} title="Acciones del ISBN" />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            {filteredProjects.length > itemsPerPage && (
                <Paginator
                    totalItems={filteredProjects.length}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={(page) => setCurrentPage(page)}
                />
            )}

            <ISBNModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                project={selectedProject}
                onSubmit={handleSubmitISBN}
                loading={loading}
            />
        </>
    );
}

export default ISBNList;
